import { useState } from 'react';
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Slider,
} from '@mui/material';
import {
  Delete,
  MoreVert,
  FlipToFront,
  FlipToBack,
  Settings,
} from '@mui/icons-material';
import type { LineElement as LineElementType } from '@/types/thinkspace';
import { ColorPalette } from '@/components/common/ColorPalette';

type LineElementProps = {
  element: LineElementType;
  selected: boolean;
  canEdit: boolean;
  onSelect: () => void;
  onUpdate: (updates: Partial<LineElementType>) => void;
  onDelete: () => void;
  onBringToFront: () => void;
  onSendToBack: () => void;
  onBringForward?: () => void;
  onSendBackward?: () => void;
  onDragStart: (e: React.MouseEvent | React.TouchEvent) => void;
};

export const LineElement = ({
  element,
  selected,
  canEdit,
  onSelect,
  onUpdate,
  onDelete,
  onBringToFront,
  onSendToBack,
  onDragStart,
}: LineElementProps) => {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [propsOpen, setPropsOpen] = useState(false);
  const [strokeColor, setStrokeColor] = useState(element.strokeColor);
  const [strokeWidth, setStrokeWidth] = useState(element.strokeWidth);

  // Calculate SVG viewBox
  const minX = Math.min(element.position.x, element.endPosition.x);
  const minY = Math.min(element.position.y, element.endPosition.y);
  const maxX = Math.max(element.position.x, element.endPosition.x);
  const maxY = Math.max(element.position.y, element.endPosition.y);
  const width = maxX - minX + element.strokeWidth * 2;
  const height = maxY - minY + element.strokeWidth * 2;

  // Relative coordinates within SVG
  const x1 = element.position.x - minX + element.strokeWidth;
  const y1 = element.position.y - minY + element.strokeWidth;
  const x2 = element.endPosition.x - minX + element.strokeWidth;
  const y2 = element.endPosition.y - minY + element.strokeWidth;

  return (
    <Box
      onClick={(e) => {
        e.stopPropagation();
        onSelect();
      }}
      onDoubleClick={(e) => {
        e.stopPropagation();
        if (canEdit) {
          setPropsOpen(true);
        }
      }}
      onMouseDown={canEdit ? onDragStart : undefined}
      onTouchStart={canEdit ? onDragStart : undefined}
      sx={{
        position: 'absolute',
        left: minX - element.strokeWidth,
        top: minY - element.strokeWidth,
        width,
        height,
        cursor: canEdit ? 'grab' : 'default',
        userSelect: 'none',
        touchAction: 'none',
        zIndex: element.zIndex,
      }}
    >
      <svg
        width={width}
        height={height}
        style={{ overflow: 'visible', pointerEvents: 'auto' }}
      >
        <line
          x1={x1}
          y1={y1}
          x2={x2}
          y2={y2}
          stroke={element.strokeColor}
          strokeWidth={element.strokeWidth}
          strokeLinecap="round"
        />
        {/* Invisible wider line for easier selection */}
        <line
          x1={x1}
          y1={y1}
          x2={x2}
          y2={y2}
          stroke="transparent"
          strokeWidth={Math.max(element.strokeWidth + 10, 20)}
          strokeLinecap="round"
          style={{ cursor: canEdit ? 'grab' : 'pointer' }}
        />
        {selected && (
          <>
            <circle cx={x1} cy={y1} r={6} fill="#0f2ccf" />
            <circle cx={x2} cy={y2} r={6} fill="#0f2ccf" />
          </>
        )}
      </svg>

      {canEdit && selected && (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setMenuAnchor(e.currentTarget);
          }}
          sx={{
            position: 'absolute',
            top: -20,
            right: -20,
            backgroundColor: 'white',
            boxShadow: 1,
            '&:hover': { backgroundColor: 'grey.100' },
          }}
        >
          <MoreVert fontSize="small" />
        </IconButton>
      )}

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem onClick={() => { setMenuAnchor(null); setPropsOpen(true); }}>
          <ListItemIcon><Settings fontSize="small" /></ListItemIcon>
          <ListItemText>Properties</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { setMenuAnchor(null); onBringToFront(); }}>
          <ListItemIcon><FlipToFront fontSize="small" /></ListItemIcon>
          <ListItemText>Bring to Front</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { setMenuAnchor(null); onSendToBack(); }}>
          <ListItemIcon><FlipToBack fontSize="small" /></ListItemIcon>
          <ListItemText>Send to Back</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { setMenuAnchor(null); onDelete(); }} sx={{ color: 'error.main' }}>
          <ListItemIcon><Delete fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Properties Dialog */}
      <Dialog open={propsOpen} onClose={() => setPropsOpen(false)} onClick={(e) => e.stopPropagation()}>
        <DialogTitle>Line Properties</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography gutterBottom>Color</Typography>
            <Box sx={{ mb: 3 }}>
              <ColorPalette
                value={strokeColor}
                onChange={setStrokeColor}
              />
            </Box>

            <Typography gutterBottom>Thickness: {strokeWidth}px</Typography>
            <Slider
              value={strokeWidth}
              onChange={(_, value) => setStrokeWidth(value as number)}
              min={1}
              max={20}
              step={1}
              valueLabelDisplay="auto"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPropsOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              onUpdate({ strokeColor, strokeWidth });
              setPropsOpen(false);
            }}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
